import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useStore } from '../store';
import { sendPurchaseComplete } from '../utils/postMessage';
import { RegisterModal } from '../components/RegisterModal';

const APP_STORE_URL = 'https://apps.apple.com/app/meeteor/id6757962427';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.thspian.meeteor';

/** Gift box icon — large at top of upsell block */
function GiftIcon({ color = 'currentColor', size = 48 }: { color?: string; size?: number }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round">
            <polyline points="20 12 20 22 4 22 4 12" />
            <rect x="2" y="7" width="20" height="5" />
            <line x1="12" y1="22" x2="12" y2="7" />
            <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
            <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
        </svg>
    );
}

/** Copy icon — for the copy button */
function CopyIcon({ color = 'currentColor', size = 20 }: { color?: string; size?: number }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
    );
}

/** Check icon — shown after copy */
function CheckIcon({ color = 'currentColor', size = 20 }: { color?: string; size?: number }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function EmailUpsellBlock({
    upsell,
    primaryColor,
}: {
    upsell: { message: string; url: string };
    primaryColor: string;
}) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(upsell.url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <div class="mw-email-upsell">
            <div class="mw-email-upsell-icon">
                <GiftIcon color={primaryColor} size={48} />
            </div>
            {upsell.message && <p class="mw-email-upsell-message">{upsell.message}</p>}
            <div class="mw-email-upsell-row">
                <input type="text" readOnly value={upsell.url} class="mw-email-upsell-input" />
                <button
                    type="button"
                    class="mw-email-upsell-copy"
                    onClick={copy}
                    aria-label={copied ? 'Copied' : 'Copy link'}>
                    {copied ? (
                        <CheckIcon color={primaryColor} size={22} />
                    ) : (
                        <CopyIcon color={primaryColor} size={22} />
                    )}
                </button>
            </div>
        </div>
    );
}

function getStoreUrl(): string {
    if (typeof navigator === 'undefined') return APP_STORE_URL;
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) return APP_STORE_URL;
    if (/android/.test(ua)) return PLAY_STORE_URL;
    return APP_STORE_URL; // default
}

function getReferralSignupUrl(referralCode: string): string {
    const isDev = typeof window !== 'undefined' && (
        window.location.hostname.includes('localhost') ||
        window.location.hostname.includes('dev.') ||
        window.location.hostname.includes('widget-dev')
    );
    const base = isDev ? 'https://dev.meeteor.me' : 'https://meeteor.me';
    return `${base}/signup?ref=${encodeURIComponent(referralCode)}`;
}

type ConfirmationProps = {
    /** When set (e.g. table modal), "Get more tickets" runs this instead of a full page reload */
    onGetMoreTickets?: () => void;
    /** Tighter padding when inside the table reservation modal */
    variant?: 'default' | 'embed';
};

export const Confirmation: FunctionalComponent<ConfirmationProps> = ({
    onGetMoreTickets,
    variant = 'default',
}) => {
    const { state, dispatch } = useStore();
    const { widget, event, purchaseResult, contact } = state;
    const [registerCompleted, setRegisterCompleted] = useState(false);
    const supportRegistration = state.supportRegistrationOverride ?? widget?.support_registration ?? true;
    const primary = widget?.theme?.primary_color || '#ef4444';
    const showRegisterBlock =
        supportRegistration &&
        !!contact.email &&
        !registerCompleted &&
        !!purchaseResult &&
        purchaseResult.shouldRegister !== false;

    useEffect(() => {
        if (purchaseResult) {
            sendPurchaseComplete({
                sale_id: purchaseResult.saleId,
                tickets: state.cart.map(c => ({
                    tier: c.tier.fee_name,
                    quantity: c.quantity,
                })),
            });
        }
    }, [purchaseResult]);

    const message = widget?.custom_success_message || 'Your tickets have been confirmed! Check your email for details.';

    return (
        <div class={variant === 'embed' ? 'mw-confirmation mw-confirmation--embed' : 'mw-confirmation'}>
            <div class="mw-confirmation-icon">✓</div>
            <h2>You're all set!</h2>
            <p>{message}</p>
            {contact.email && (
                <p style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
                    Confirmation sent to <strong>{contact.email}</strong>
                </p>
            )}

            {showRegisterBlock && (
                <div
                    class="mw-register-prompt"
                    style={
                        {
                            '--mw-register-accent': primary,
                        } as Record<string, string>
                    }>
                    <p class="mw-register-prompt-eyebrow">Next step</p>
                    <h3 class="mw-register-prompt-title">Create your Meeteor password</h3>
                    <p class="mw-register-prompt-text">
                        Use the secure form to add a password to this email — unlock the app, refunds, and event updates in
                        one place.
                    </p>
                </div>
            )}

            {event?.short_url && (
                <a
                    href={event.short_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mw-btn mw-btn--outline"
                    style={{ marginTop: '1.5rem', display: 'inline-flex' }}>
                    View Event
                </a>
            )}

            {/* App download card */}
            <div class="mw-app-download-card">
                <div class="mw-app-download-decor" aria-hidden="true">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1"
                        viewBox="0 0 24 24">
                        <circle cx="18" cy="5" r="3" />
                        <circle cx="6" cy="12" r="3" />
                        <circle cx="18" cy="19" r="3" />
                        <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
                        <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
                    </svg>
                </div>
                <div class="mw-app-download-header">
                    <div class="mw-app-download-logo">
                        <img src="/meeteor-logo.png" alt="Meeteor" />
                    </div>
                    <div class="mw-app-download-heading">
                        <h3 class="mw-app-download-title">Manage your event experience with Meeteor</h3>
                        <p class="mw-app-download-subtitle">The ultimate event companion</p>
                    </div>
                </div>
                <div class="mw-app-download-body">
                    <p class="mw-app-download-desc">
                        Handle refunds, view event details, and get real-time updates all in one place.{' '}
                        {event?.name ? `Don't miss a beat at ${event.name}` : "Don't miss a beat at your next event."}
                    </p>
                </div>
                <div class="mw-app-download-actions">
                    <a href={getStoreUrl()} target="_blank" rel="noopener noreferrer" class="mw-app-download-btn">
                        Download Now
                    </a>
                </div>
                <div class="mw-app-download-platforms">
                    <span class="mw-app-download-available">Available on</span>
                    <div class="mw-app-download-badges">
                        <a
                            href={APP_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="mw-app-badge"
                            aria-label="App Store">
                            <img src="https://df41u66howm6r.cloudfront.net/app/app-store.png" alt="App Store" />
                        </a>
                        <a
                            href={PLAY_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="mw-app-badge"
                            aria-label="Google Play">
                            <img src="https://df41u66howm6r.cloudfront.net/app/g-store.png" alt="Google Play" />
                        </a>
                    </div>
                </div>
            </div>

            {/* Email upsell — share link with copy (before registration or when no user referral) */}
            {purchaseResult?.email_upsell && !state.userReferralCode && (
                <EmailUpsellBlock
                    upsell={purchaseResult.email_upsell}
                    primaryColor={widget?.theme?.primary_color || '#ef4444'}
                />
            )}

            {/* Post-registration gift — Claim up to X points and redeem prizes (uses loyalty_config when available) */}
            {registerCompleted && state.userReferralCode && (() => {
                const lc = event?.loyalty_config;
                const hasLoyalty = lc?.enabled && lc?.referral_signup_points != null && (lc?.points_per_ticket != null || lc?.referral_purchase_points != null);
                const totalPoints = hasLoyalty
                    ? (lc.referral_signup_points ?? 0) + (lc.referral_purchase_points ?? lc.points_per_ticket ?? 0)
                    : 100;
                const message = hasLoyalty
                    ? `Claim up to ${totalPoints} points and redeem prizes when you share the link with your friends`
                    : 'Claim up to 100 points when you refer your friends to sign up';
                return (
                    <EmailUpsellBlock
                        upsell={{
                            message,
                            url: getReferralSignupUrl(state.userReferralCode),
                        }}
                        primaryColor={widget?.theme?.primary_color || '#ef4444'}
                    />
                );
            })()}

            {/* Registration modal — when shouldRegister */}
            {showRegisterBlock && !registerCompleted && contact.email && (
                <RegisterModal
                    email={contact.email}
                    name={contact.name}
                    phone={contact.phone || undefined}
                    referralCode={state.refCode || undefined}
                    primaryColor={primary}
                    onClose={() => setRegisterCompleted(true)}
                    onComplete={(userReferralCode) => {
                        if (userReferralCode) dispatch({ type: 'SET_USER_REFERRAL_CODE', code: userReferralCode });
                        setRegisterCompleted(true);
                    }}
                />
            )}

            {/* Get more tickets — reloads the widget */}
            <button
                type="button"
                class="mw-btn-get-more"
                onClick={() => (onGetMoreTickets ? onGetMoreTickets() : window.location.reload())}
            >
                Get more tickets
            </button>

            <div class="mw-powered" style={{ marginTop: '1.5rem' }}>
                Powered by{' '}
                <a href="https://meeteor.me" target="_blank" rel="noopener noreferrer">
                    Meeteor
                </a>
            </div>
        </div>
    );
};
